/**
 * The template editor and preview (doc 05-admin/26 §3.1 items 4–5): write a new version of one
 * event × channel × locale, see it against the version it replaces, and render any version
 * through the real send path.
 *
 * **Built from the spec, not a design.** The design's Templates tab is a read-only table; the
 * owner ruled on 2026-10-26 that design-less screens are built from the docs with the console's
 * own components and restyled later.
 *
 * Saving never sends: a new version lands as a draft and is activated from the table, so a
 * half-finished sentence cannot reach anybody. The content lint runs on save, and its refusal is
 * shown verbatim. The test-send to the override address (§3.1 item 5) has no endpoint yet.
 */
import { useState } from 'react';
import { Chip, StatusChip } from '../components/primitives';
import { Input } from '../components/forms';
import { ErrorState } from '../components/feedback';
import { ConfirmAction, DiffView, ScopeAction, Section } from '../components/kit';
import { useConsole } from '../app/console';
import { errorMessage } from '../lib/api/client';
import { useCreateTemplate, usePreviewTemplate, useTestSendTemplate, type TemplateRow } from '../lib/api/hooks/notifications';

/** The context tokens a body uses, in order of first use: `{{ puja_name }}` → `puja_name`. */
export function templateTokens(...texts: readonly (string | null | undefined)[]): string[] {
  const seen: string[] = [];
  for (const text of texts) {
    for (const match of (text ?? '').matchAll(/\{\{\s*([a-zA-Z_][a-zA-Z0-9_]*)/g)) {
      if (!seen.includes(match[1]!)) seen.push(match[1]!);
    }
  }
  return seen;
}

/** Every version of the same event × channel × locale, newest first. */
export function versionsOf(all: readonly TemplateRow[], row: TemplateRow): TemplateRow[] {
  return all
    .filter((t) => t.event_type === row.event_type && t.channel === row.channel && t.locale === row.locale)
    .sort((a, b) => b.version - a.version);
}

/**
 * True when an SMS edit moves the body off the shape registered on DLT. The registry is matched
 * at the operator, so a drifted body is refused at send time; this warns while it can be fixed.
 */
export function dltDrift(base: TemplateRow, body: string): boolean {
  return base.channel === 'sms' && Boolean(base.dlt_template_id) && body.trim() !== base.body.trim();
}

const hasSubject = (channel: string) => channel === 'email' || channel === 'push';

export function TemplateEditor({
  base,
  all,
  onClose,
}: {
  base: TemplateRow;
  all: readonly TemplateRow[];
  onClose: () => void;
}) {
  const { scope, toast } = useConsole();
  const [subject, setSubject] = useState(base.subject ?? '');
  const [body, setBody] = useState(base.body);
  const create = useCreateTemplate();
  const history = versionsOf(all, base);
  const unchanged = body === base.body && subject === (base.subject ?? '');

  return (
    <Section
      title={`New version · ${base.event_type} · ${base.channel} · ${base.locale}`}
      note={`From v${base.version} — saves as a draft, activated from the table`}
      actions={
        <ScopeAction scope={scope} need="growth" size="sm" variant="ghost" onClick={onClose}>
          Close
        </ScopeAction>
      }
    >
      <div style={{ display: 'grid', gap: 'var(--sp-md)' }}>
        {hasSubject(base.channel) && (
          <Input label={base.channel === 'push' ? 'Title' : 'Subject'} value={subject} onChange={setSubject} />
        )}
        <Input label="Body" multiline value={body} onChange={setBody} />
        {templateTokens(base.subject, base.body).length > 0 && (
          <div style={{ display: 'flex', gap: 'var(--sp-xs)', flexWrap: 'wrap', alignItems: 'center' }}>
            <span style={{ fontSize: 'var(--fs-admin-colhead)', color: 'var(--c-ink3)' }}>Tokens this event's copy uses:</span>
            {templateTokens(base.subject, base.body).map((t) => (
              <Chip key={t} label={`{{ ${t} }}`} />
            ))}
          </div>
        )}
        {base.channel === 'sms' && (
          <p style={{ margin: 0, fontSize: 'var(--fs-admin-ui)' }}>
            DLT template id: <span className="mono-num">{base.dlt_template_id ?? 'none registered'}</span> — read-only; it is the
            operator's registration, not ours.
          </p>
        )}
        {dltDrift(base, body) && (
          <StatusChip tone="warn" label="This body no longer matches its DLT registration — the operator will refuse it at send time until it is re-registered" />
        )}
        {base.channel === 'whatsapp' && (
          <p style={{ margin: 0, fontSize: 'var(--fs-admin-ui)' }}>
            WhatsApp template {base.whatsapp_template_name ?? '(unnamed)'} · BSP approval {base.whatsapp_approval_status ?? 'not submitted'}. A
            new version activates only once the BSP approves it.
          </p>
        )}

        <DiffView
          labels={[`v${base.version}`, 'New draft']}
          rows={[
            ...(hasSubject(base.channel) ? [[base.channel === 'push' ? 'Title' : 'Subject', base.subject ?? '—', subject || '—'] as const] : []),
            ['Body', base.body, body],
          ]}
        />

        {create.isError && <ErrorState inline title="Not saved" cause={errorMessage(create.error)} />}
        <div>
          <ScopeAction
            scope={scope}
            need="growth"
            disabled={unchanged || body.trim() === ''}
            onClick={() =>
              create.mutate(
                {
                  event_type: base.event_type,
                  channel: base.channel,
                  locale: base.locale,
                  body,
                  subject: hasSubject(base.channel) ? subject || null : null,
                  dlt_template_id: base.dlt_template_id ?? null,
                  provider_template_id: base.provider_template_id ?? null,
                  whatsapp_template_name: base.whatsapp_template_name ?? null,
                  whatsapp_approval_status: base.whatsapp_approval_status ?? null,
                },
                {
                  onSuccess: (row) => {
                    toast({ tone: 'success', message: `Saved as draft v${row.version} — activate it from the table` });
                    onClose();
                  },
                },
              )
            }
          >
            Save as draft
          </ScopeAction>
        </div>

        <div>
          <span style={{ fontSize: 'var(--fs-admin-colhead)', color: 'var(--c-ink3)' }}>Version history — sent messages keep the version they used</span>
          <ul style={{ margin: 'var(--sp-xs) 0 0', paddingLeft: 'var(--sp-lg)', fontSize: 'var(--fs-admin-ui)' }}>
            {history.map((v) => (
              <li key={v.id}>
                v{v.version} · {v.is_active ? 'live' : 'draft'} · {v.body.slice(0, 80)}
                {v.body.length > 80 ? '…' : ''}
              </li>
            ))}
          </ul>
        </div>
      </div>
    </Section>
  );
}

export function TemplatePreviewPanel({ template, onClose }: { template: TemplateRow; onClose: () => void }) {
  const { scope } = useConsole();
  const tokens = templateTokens(template.subject, template.body);
  const [context, setContext] = useState<Record<string, string>>({});
  const preview = usePreviewTemplate();
  const testSend = useTestSendTemplate();
  const [confirmingSend, setConfirmingSend] = useState(false);
  const slots = preview.data?.whatsapp_variables ?? [];

  return (
    <Section
      title={`Preview · ${template.event_type} · ${template.channel} · ${template.locale} · v${template.version}`}
      note="Rendered by the real send path — a token that would fail at send fails here"
      actions={
        <ScopeAction scope={scope} need="growth" size="sm" variant="ghost" onClick={onClose}>
          Close
        </ScopeAction>
      }
    >
      <div style={{ display: 'grid', gap: 'var(--sp-md)' }}>
        {tokens.length === 0 ? (
          <span style={{ fontSize: 'var(--fs-admin-ui)', color: 'var(--c-ink2)' }}>This version uses no tokens.</span>
        ) : (
          <div style={{ display: 'flex', gap: 'var(--sp-md)', flexWrap: 'wrap' }}>
            {tokens.map((t) => (
              <Input key={t} label={t} value={context[t] ?? ''} onChange={(value) => setContext((prev) => ({ ...prev, [t]: value }))} />
            ))}
          </div>
        )}
        <div>
          <ScopeAction
            scope={scope}
            need="growth"
            size="sm"
            onClick={() => preview.mutate({ templateId: template.id, context })}
          >
            Render preview
          </ScopeAction>{' '}
          <ScopeAction
            scope={scope}
            need="growth"
            size="sm"
            variant="secondary"
            disabled={testSend.isPending}
            onClick={() => setConfirmingSend(true)}
          >
            Send test to the override address
          </ScopeAction>
        </div>
        {confirmingSend && (
          // A test-send is a real message through the live provider, so it is typed, not clicked.
          <ConfirmAction
            open
            title={`Send v${template.version} (${template.channel}, ${template.locale}) as a real message?`}
            phrase="SEND"
            consequences={[
              "It goes through this environment's live provider to the override address only — never to the sample context's names.",
              'A provider message is real: it counts against quotas and DLT, and the send is audited with your name.',
            ]}
            confirmLabel="Send test"
            onCancel={() => setConfirmingSend(false)}
            onConfirm={() => {
              testSend.mutate({ templateId: template.id, context });
              setConfirmingSend(false);
            }}
          />
        )}
        {testSend.isError && <ErrorState inline title="No test-send" cause={errorMessage(testSend.error)} />}
        {testSend.data && (
          <span role="note" style={{ fontSize: 'var(--fs-admin-ui)', color: testSend.data.sent ? 'var(--c-success-text)' : 'var(--c-error-text)' }}>
            {testSend.data.sent
              ? `Sent v${testSend.data.template_version} to ${testSend.data.destination} via ${testSend.data.provider}.`
              : `Not sent to ${testSend.data.destination} — ${testSend.data.provider} said ${testSend.data.error ?? 'no reason'}.`}
          </span>
        )}
        {preview.isError && <ErrorState inline title="Would not render" cause={errorMessage(preview.error)} />}
        {preview.data && (
          <div style={{ display: 'grid', gap: 'var(--sp-sm)', fontSize: 'var(--fs-admin-ui)' }}>
            {preview.data.subject && (
              <div>
                <strong>{template.channel === 'push' ? 'Title' : 'Subject'}:</strong> <span lang={template.locale}>{preview.data.subject}</span>
              </div>
            )}
            <div lang={template.locale} style={{ whiteSpace: 'pre-wrap' }} data-testid="preview-body">
              {preview.data.body}
            </div>
            {preview.data.text_part && (
              <details>
                <summary>Text part, as a text-only mail client shows it</summary>
                <pre style={{ whiteSpace: 'pre-wrap' }}>{preview.data.text_part}</pre>
              </details>
            )}
            {preview.data.html_part && (
              // Sandboxed with no permissions: the email is shown, never run.
              <iframe title="Email as sent" sandbox="" srcDoc={preview.data.html_part} style={{ width: '100%', height: 420, border: '1px solid var(--c-border)' }} />
            )}
            {slots.length > 0 && (
              <div>
                <strong>WhatsApp variables, in slot order:</strong> {slots.join(' · ')}
              </div>
            )}
          </div>
        )}
      </div>
    </Section>
  );
}
